import * as React from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import deLocale from "@fullcalendar/core/locales/de";

import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Alert,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useDataProvider, useNotify } from "react-admin";
import { useNavigate } from "react-router-dom";
import { useClub } from "../club/ClubContext";

type Occurrence = {
  id: number;
  title: string;
  starts_at: string;
  ends_at: string;
  timezone?: string;
  status?: string;
  team_names?: string[];
};

function toIsoDate(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export default function CalendarPage() {
  const { clubId } = useClub();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const navigate = useNavigate();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const calendarRef = React.useRef<FullCalendar | null>(null);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [events, setEvents] = React.useState<any[]>([]);

  const fetchRange = React.useCallback(
    async (rangeStart: Date, rangeEnd: Date) => {
      if (!clubId) return;

      setLoading(true);
      setError(null);

      try {
        const from_dt = toIsoDate(rangeStart);
        const to_dt = toIsoDate(rangeEnd);

        const res = await dataProvider.getList<Occurrence>("events/occurrences", {
          pagination: { page: 1, perPage: 2000 },
          sort: { field: "starts_at", order: "ASC" },
          filter: {
            club_id: clubId,
            from_dt,
            to_dt,
            include_past: 1,
          },
        } as any);

        const mapped = (res.data ?? []).map((o: Occurrence) => ({
          id: o.id,
          title: o.title,
          start: o.starts_at,
          end: o.ends_at,
          extendedProps: {
            timezone: o.timezone,
            status: o.status,
            team_names: o.team_names,
          },
        }));

        setEvents(mapped);
      } catch (e: any) {
        const msg = e?.message ?? "Kalender konnte nicht geladen werden";
        setError(msg);
        notify(msg, { type: "warning" });
      } finally {
        setLoading(false);
      }
    },
    [clubId, dataProvider, notify]
  );

  // ✅ Beim Wechsel Club: Kalender neu laden
  React.useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api || !clubId) return;
    fetchRange(api.view.activeStart, api.view.activeEnd);
  }, [clubId, fetchRange]);

  return (
    <Box sx={{ p: isXs ? 1 : 2 }}>
      <Stack spacing={isXs ? 1 : 2}>
        <Typography variant={isXs ? "h6" : "h5"} sx={{ fontWeight: 900 }}>
          Kalender
        </Typography>

        {!clubId && <Alert severity="info">Bitte zuerst einen Club auswählen.</Alert>}
        {error && <Alert severity="warning">{error}</Alert>}

        <Card>
         <CardContent
  sx={{
    p: isXs ? 1 : 2,

    ...(isXs
      ? {
          "& .fc .fc-toolbar": {
            marginBottom: "0.35rem",
          },
          "& .fc .fc-toolbar-title": {
            fontSize: "1.2rem",
            fontWeight: 800,
          },
          "& .fc .fc-button": {
            padding: "0.25rem 0.5rem",
            fontSize: "0.75rem",
            lineHeight: 1.2,
          },
          "& .fc .fc-button .fc-icon": {
            fontSize: "1rem",
          },
          "& .fc .fc-toolbar-chunk": {
            display: "flex",
            gap: "0.35rem",
            alignItems: "center",
          },
          "& .fc .fc-daygrid-day-number": {
            fontSize: "0.75rem",
          },
        }
      : {}),
  }}
>
            {loading && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2">Lade Termine…</Typography>
              </Stack>
            )}

            <FullCalendar
              ref={calendarRef as any}
              locale={deLocale}
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
              initialView={isXs ? "listWeek" : "timeGridWeek"}
              datesSet={(arg) => {
                fetchRange(arg.view.activeStart, arg.view.activeEnd);
              }}
              headerToolbar={{
                left: "prev,next",
                center: "title",
                right: isXs ? "dayGridMonth,listWeek" : "timeGridWeek,dayGridMonth,listWeek",
              }}
              buttonText={{
                today: "Heute",
                week: "Woche",
                month: "Monat",
                list: "Liste",
              }}
              firstDay={1}
              nowIndicator
              height="auto"
              weekends
              // ✅ Mobile: Week-List statt Timeslots (viel kompakter)
              allDaySlot={false}
              slotMinTime={isXs ? "08:00:00" : "06:00:00"}
              slotMaxTime={isXs ? "22:00:00" : "24:00:00"}
              expandRows={false}
              stickyHeaderDates={!isXs}
              dayMaxEvents={true}
              eventTimeFormat={{
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              }}
              events={events}
              eventClick={(info) => {
                navigate(`/events/occurrences/${info.event.id}/show`);
              }}
              // ✅ etwas kompaktere Darstellung
              eventDisplay="block"
              aspectRatio={isXs ? 0.9 : 1.35}
            />
          </CardContent>
        </Card>

        {isXs && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Tipp: Für Mobil ist „Liste“ oft am besten. Monat zeigt nur die Tage, Woche ist als Liste übersichtlicher.
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
